import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';

import { AGURenderService } from '../../app-surface/render.service';
import { AGUViewportService } from '../../viewport/phone';

/** The toast's one action — the prototype's "View" link to Saved (P/Member_Hub.html:955). */
export interface AGUToastAction {
    readonly Label: string;
    readonly Run: () => void;
}

/** What the toast shows. `Text` outlives `Visible` so the bar fades out with its words still on it. */
export interface AGUToastState {
    readonly Text: string;
    readonly Action: AGUToastAction | null;
    readonly Visible: boolean;
}

/** How long a toast stays up: 2.6 s, the prototype's own timer (`setTimeout(…, 2600)`, P/Member_Hub.html:956). */
export const TOAST_VISIBLE_MS = 2600;
/** The bookmark toast's copy, verbatim (P/Member_Hub.html:964). */
export const SAVED_TOAST_TEXT = 'Saved to your list';
export const REMOVED_TOAST_TEXT = 'Removed from Saved';
/** The Saved toast's link (P/Member_Hub.html:955). */
export const SAVED_TOAST_ACTION_LABEL = 'View';

const HIDDEN: AGUToastState = { Text: '', Action: null, Visible: false };

/**
 * The phone shell's toast (`.mtoast`, P/Member_Hub.html:702-706, 952-966): one short message at a time, gone after
 * `TOAST_VISIBLE_MS`, with at most one action.
 *
 * Provided by each surface component (like `AGUViewportService`), which renders `<agu-toast>` once; modules reach it through the
 * element injector. It belongs to the phone layout — the prototype's handler returns unless `html.mob` (:959) — so `Show` does nothing
 * while a viewport service says the surface is not on a phone. The surface registers `OpenSaved` so a save from anywhere can offer
 * "View" without each caller knowing how to reach the Saved board.
 *
 * Zoneless host: the timed hide is not a user event, so every state change asks `AGURenderService.Notify()` for its repaint.
 */
@Injectable()
export class AGUToastService implements OnDestroy {
    private state: AGUToastState = HIDDEN;
    private timer: ReturnType<typeof setTimeout> | null = null;

    /** Opens the Saved board; set by the surface when its app has one, null otherwise (the Saved toast then has no "View"). */
    OpenSaved: (() => void) | null = null;

    constructor(
        @Optional() @Inject(AGURenderService) private readonly render: AGURenderService | null,
        @Optional() @Inject(AGUViewportService) private readonly viewport: AGUViewportService | null
    ) {}

    get State(): AGUToastState {
        return this.state;
    }

    /** Shows `text` (and `action`) for `TOAST_VISIBLE_MS`, replacing whatever was up and restarting the window (:956). */
    Show(text: string, action?: AGUToastAction): void {
        if (this.viewport && !this.viewport.IsPhone) return;
        this.state = { Text: text, Action: action ?? null, Visible: true };
        this.ClearTimer();
        this.timer = setTimeout(() => {
            this.timer = null;
            this.Hide();
        }, TOAST_VISIBLE_MS);
        this.render?.Notify();
    }

    /** The bookmark toast (:963-964): "Saved to your list" with View, or "Removed from Saved" alone. */
    ShowSaved(saved: boolean): void {
        if (saved) {
            const openSaved = this.OpenSaved;
            this.Show(SAVED_TOAST_TEXT, openSaved ? { Label: SAVED_TOAST_ACTION_LABEL, Run: openSaved } : undefined);
        } else {
            this.Show(REMOVED_TOAST_TEXT);
        }
    }

    /** The toast's action button: runs the action, then the toast leaves with the screen it pointed away from. */
    RunAction(): void {
        const action = this.state.Action;
        this.Hide();
        action?.Run();
    }

    Hide(): void {
        this.ClearTimer();
        if (!this.state.Visible) return;
        this.state = { ...this.state, Visible: false };
        this.render?.Notify();
    }

    ngOnDestroy(): void {
        this.ClearTimer();
    }

    private ClearTimer(): void {
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }
}
